import * as React from "react";
import { bindActionCreators } from "redux";
import axios from "axios";
import memoize from "lodash/memoize";
import { connect } from "react-redux";
// interfaces
import { IAppState } from "app/rootReducer";
import { AppDispatch } from "app/store";
// components
import { Collections } from "common/components/overlayTooltip/preset";
import { pushToast as pushToastAction } from "@vingle/wtf-sdk/actions/toast";
// actions
import {
  getMyCollections as getMyCollectionsAction,
  getClippedCollectionsFromCard as getClippedCollectionsFromCardAction,
  saveNewCollection as saveNewCollectionAction,
  saveCollections as saveCollectionsAction,
} from "@vingle/wtf-sdk/actions/collectionDialog";

function mapStateToProps(state: IAppState) {
  return {
    currentUser: state.app.currentUser,
    meta: state.collectionDialog.meta,
    collections: state.collectionDialog.data.collections,
    clippedCollections: state.collectionDialog.data.selectedCollections,
  };
}

function mapDispatchToProps(dispatch: AppDispatch) {
  return bindActionCreators(
    {
      getMyCollections: getMyCollectionsAction,
      getClippedCollectionsFromCard: getClippedCollectionsFromCardAction,
      saveNewCollection: saveNewCollectionAction,
      saveCollections: saveCollectionsAction,
      pushToast: pushToastAction,
    },
    dispatch,
  );
}

interface IProps
  extends ReturnType<typeof mapStateToProps>,
    ReturnType<typeof mapDispatchToProps> {
  postId: Vingle.Id;
  onCancel?: VoidFunction;
}

class CollectionTooltipContainer extends React.PureComponent<IProps> {
  private cancelTokenSource = axios.CancelToken.source();
  private saveCancelTokenSource = axios.CancelToken.source();

  public componentDidMount() {
    this.getData(true);
  }

  public componentDidUpdate(prevProps: IProps) {
    const { currentUser, postId } = this.props;
    if (prevProps.postId !== postId || prevProps.currentUser !== currentUser) {
      this.getData();
    }
  }

  public componentWillUnmount() {
    this.cancelTokenSource.cancel();
  }

  public render() {
    const { collections, clippedCollections } = this.props;
    const { isLoading, isSaving } = this.props.meta;
    const initialSelectedCollection = collectionIdFlatten(clippedCollections);

    return (
      <Collections
        isLoading={isLoading}
        isSaving={isSaving}
        collections={collections}
        initialSelectedCollection={initialSelectedCollection}
        onNewItemCreate={this.createNewCollection}
        onSave={this.saveToCollection}
        onCancel={this.handleCancel}
      />
    );
  }

  private readonly getData = (updateOnly: boolean = false) => {
    const {
      currentUser,
      postId,
      collections,
      getMyCollections,
      getClippedCollectionsFromCard,
    } = this.props;

    if (currentUser) {
      this.cancelAndRenewal();
      if (updateOnly && collections.length > 0) {
        getClippedCollectionsFromCard(postId, this.cancelTokenSource.token);
        return;
      }

      getMyCollections({
        username: currentUser.username,
        postId,
        cancelToken: this.cancelTokenSource.token,
      });

      getClippedCollectionsFromCard(postId, this.cancelTokenSource.token);
    }
  };

  private readonly createNewCollection = (value: string) => {
    const { saveNewCollection } = this.props;
    saveNewCollection(value, this.cancelTokenSource.token);
  };

  private readonly saveToCollection = (selectedCollection: Vingle.Id[]) => {
    const { saveCollections, postId } = this.props;
    saveCollections({
      cardId: postId,
      collectionIds: selectedCollection,
      cancelToken: this.saveCancelTokenSource.token,
      afterSuccess: this.handleAfterSave,
    });

    this.handleCancel();
  };

  private readonly handleAfterSave = () => {
    this.props.pushToast({
      type: "SUCCESS",
      messageId: "clip_success",
    });
  };

  private readonly handleCancel = () => {
    if (this.props.onCancel) this.props.onCancel();
  };

  private readonly cancelAndRenewal = () => {
    this.cancelTokenSource.cancel();
    this.cancelTokenSource = axios.CancelToken.source();
  };
}

const collectionIdFlatten = memoize(
  (collections: ReadonlyArray<Vingle.Collection.ICollection>) => {
    return collections.map(collection => collection.id);
  },
);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CollectionTooltipContainer);
